import { useState, useEffect, useRef } from 'react';
import './login.css';
import { useNavigate } from 'react-router-dom';

// ─── Sub-components ───────────────────────────────────────────

const ParkGuardLogo = () => (
  <div className="logo-wrap">
    <div className="logo-icon">
      <svg viewBox="0 0 52 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M26 2L48 12V32C48 44 38 54 26 58C14 54 4 44 4 32V12L26 2Z"
          fill="rgba(26,111,196,0.25)"
          stroke="#3b9eff"
          strokeWidth="1.5"
        />
        <rect x="14" y="28" width="24" height="14" rx="2"
          fill="rgba(59,158,255,0.15)" stroke="#3b9eff" strokeWidth="1" />
        <rect x="18" y="31" width="6" height="5" rx="1"
          fill="rgba(59,158,255,0.4)" stroke="#3b9eff" strokeWidth="0.8" />
        <rect x="28" y="31" width="6" height="5" rx="1"
          fill="rgba(59,158,255,0.4)" stroke="#3b9eff" strokeWidth="0.8" />
        <circle cx="26" cy="22" r="5"
          fill="rgba(59,158,255,0.15)" stroke="#3b9eff" strokeWidth="1" />
        <circle cx="26" cy="22" r="2" fill="#3b9eff" />
        <circle cx="17" cy="42" r="3" fill="#0a1929" stroke="#3b9eff" strokeWidth="1" />
        <circle cx="35" cy="42" r="3" fill="#0a1929" stroke="#3b9eff" strokeWidth="1" />
      </svg>
    </div>
    <div className="logo-text">
      <div className="logo-name">
        PARK<span>GUARD</span>
      </div>
      <div className="logo-sub">Gestion Parking PL</div>
    </div>
  </div>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Scene (background animation) ────────────────────────────

const ParkingScene = () => {
  const roles = [
    { id: 'veilleur', label: 'Veilleur', icon: '🌙' },
    { id: 'caissier', label: 'Caissier', icon: '💳' },
    { id: 'admin',    label: 'Admin',    icon: '⚙️'  },
  ];

  return (
    <div className="scene" aria-hidden="true">
      <div className="sky" />
      <div className="stars" />

      {/* Buildings */}
      <div className="buildings">
        {[
          { w: 40,  h: 80  },
          { w: 60,  h: 130 },
          { w: 28,  h: 55  },
          { w: 80,  h: 100 },
          { w: 35,  h: 70, ml: 20 },
          { w: 55,  h: 110 },
          { w: 70,  h: 90, mr: '8%' },
        ].map((b, i) => (
          <div
            key={i}
            className="building"
            style={{
              width: b.w,
              height: b.h,
              marginLeft: b.ml,
              marginRight: b.mr,
            }}
          />
        ))}
      </div>

      <div className="horizon" />
      <div className="ground" />
      <div className="asphalt" />

      {/* Lamp posts */}
      {[
        { left: '12%',  height: 120, delay: '0s'  },
        { left: '38%',  height: 130, delay: '-2s' },
        { right: '30%', height: 120, delay: '-4s' },
        { right: '8%',  height: 125, delay: '-1s' },
      ].map((lamp, i) => (
        <div
          key={i}
          className="lamp-pole"
          style={{ left: lamp.left, right: lamp.right, height: lamp.height }}
        >
          <div className="lamp-head" />
          <div className="lamp-light" style={{ animationDelay: lamp.delay }} />
          <div className="lamp-cone" />
        </div>
      ))}

      {/* Truck */}
      <div className="truck-wrap">
        <div className="truck">
          <div className="truck-shadow" />
          <div className="truck-trailer" />
          <div className="truck-cabin">
            <div className="truck-windshield" />
          </div>
          <div className="truck-base" />
          <div className="headlight headlight-top" />
          <div className="headlight headlight-bottom" />
          {['w1','w2','w3','w4','w5','w6'].map(cls => (
            <div key={cls} className={`wheel ${cls}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main Login Component ──────────────────────────────────────

const ROLES = [
  { id: 'veilleur', label: 'Veilleur', icon: '🌙' },
  { id: 'caissier', label: 'Caissier', icon: '💳' },
  { id: 'admin',    label: 'Admin',    icon: '⚙️'  },
];

const Login = ({ onLogin }) => {
  const [selectedRole, setSelectedRole] = useState('veilleur');
  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [errors, setErrors]             = useState({});
  const [loading, setLoading]           = useState(false);
  const [success, setSuccess]           = useState(false);
  const usernameRef = useRef(null);
const navigate=useNavigate()

  // Focus first field on mount
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  // Enter key submits
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter' && !loading && !success) handleSubmit();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [username, password, selectedRole, loading, success]);


  const getNewPage =()=>{
    navigate("/Veilleur")
  }
  const validate = () => {
    const errs = {};
    if (!username.trim()) errs.username = true;
    if (!password.trim()) errs.password = true;
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTimeout(() => setErrors({}), 1500);
      return;
    }

    setLoading(true);

    try {
      // ── Replace this block with your real API call ──
      // const res = await axios.post('/api/auth/login', {
      //   username, password, role: selectedRole
      // });
      // const { token, user } = res.data;
      // localStorage.setItem('pg_token', token);
      // onLogin?.(user);
      // ────────────────────────────────────────────────

      // Simulated delay (remove in production)
      await new Promise(r => setTimeout(r, 1600));

      setLoading(false);
      setSuccess(true);

      // Redirect after success animation
      setTimeout(() => {
        onLogin?.({ username, role: selectedRole });
      }, 1800);

    } catch (err) {
      setLoading(false);
      setErrors({ api: err?.response?.data?.message || 'Identifiants incorrects' });
      setTimeout(() => setErrors({}), 3000);
    }
  };

  const roleLabel = ROLES.find(r => r.id === selectedRole)?.label ?? '';

  return (
    <div className="pg-page">
      <ParkingScene />

      <div className="pg-card">

        {/* Success overlay */}
        <div className={`success-overlay ${success ? 'show' : ''}`}>
          <div className="success-check">
            <CheckIcon />
          </div>
          <div className="success-label">Connexion réussie</div>
          <div className="success-sub">Bienvenue — profil {roleLabel}</div>
        </div>

        <ParkGuardLogo />
        <button onClick={getNewPage}></button>

        <p className="form-title">Connexion</p>

        {/* Role selector */}
        <div className="role-grid" role="group" aria-label="Sélection du profil">
          {/* {ROLES.map(role => (
            <button
              key={role.id}
              className={`role-btn ${selectedRole === role.id ? 'active' : ''}`}
              onClick={() => setSelectedRole(role.id)}
              type="button"
              aria-pressed={selectedRole === role.id}
            >
              <span className="role-icon" aria-hidden="true">{role.icon}</span>
              {role.label}
            </button>
          ))}
           */}

        </div>


        {/* API error banner */}
        {errors.api && (
          <div className="error-banner" role="alert">{errors.api}</div>
        )}

        {/* Username */}
        <div className="field">
          <label htmlFor="pg-username">Identifiant</label>
          <div className="input-wrap">
            <input
              id="pg-username"
              ref={usernameRef}
              type="text"
              autoComplete="username"
              placeholder="Votre identifiant"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className={errors.username ? 'error' : ''}
              disabled={loading || success}
            />  
              <UserIcon />  
              </div>
        </div>


        {/* Password */}
        <div className="field">
          <label htmlFor="pg-password">Mot de passe</label>
       
          <div className="input-wrap">
            <input
              id="pg-password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className={errors.password ? 'error' : ''}
              disabled={loading || success}
            />
            <LockIcon />
          </div>
        </div>

        {/* Submit */}
        <button
          className={`btn-submit ${loading ? 'loading' : ''}`}
          onClick={handleSubmit}
          disabled={loading || success}
          type="button"
        >
          <span className="btn-text">Se connecter</span>
          <span className="btn-spinner">
            <div className="spinner-ring" />
          </span>
        </button>

        <p className="footer-note">ParkGuard v1.0 — Accès sécurisé</p>
      </div>
    </div>
  );
};

export default Login;